import React from 'react';
import {Box, styled} from '@mui/material';
import {CaptionsStylePreset} from '@contentlab/types/captions';
import translate from '@/utils/translate';
import {
  PositionButtonGroup,
  PositionAlignButton,
  Icon,
  Stack,
  Body,
  StyledSlider,
} from './SharedConstants';

interface PositionSectionProps {
  updateNormalizedVerticalPosition: (position: number) => void;
  editedStyle: CaptionsStylePreset;
}

const SectionLabel = styled(Body)(() => ({
  marginBottom: '16px',
}));

// Default values
const DEFAULT_NORMALIZED_VERTICAL_POSITION = 0.8;
const POSITION_TOP = 0.2;
const POSITION_CENTER = 0.5;
const POSITION_BOTTOM = 0.8;
const POSITION_TOLERANCE = 0.1;

// Helper functions to work with normalized position presets
const getSelectedPreset = (
  normalized: number
): 'top' | 'center' | 'bottom' | null => {
  if (Math.abs(normalized - POSITION_TOP) <= POSITION_TOLERANCE) return 'top';
  if (Math.abs(normalized - POSITION_CENTER) <= POSITION_TOLERANCE)
    return 'center';
  if (Math.abs(normalized - POSITION_BOTTOM) <= POSITION_TOLERANCE)
    return 'bottom';
  return null;
};

function PositionPresetsSection({
  updateNormalizedVerticalPosition,
  editedStyle,
}: PositionSectionProps) {
  const currentNormalized =
    editedStyle.captionsStyle.normalizedVerticalPosition ??
    DEFAULT_NORMALIZED_VERTICAL_POSITION;
  const selectedPreset = getSelectedPreset(currentNormalized);

  return (
    <Box>
      <Stack gap="24px">
        <PositionButtonGroup>
          <PositionAlignButton
            appearance="neutral"
            mode="tinted"
            size="small"
            selected={selectedPreset === 'top'}
            onClick={() => updateNormalizedVerticalPosition(POSITION_TOP)}
          >
            <Icon
              name="StateBar-Position-Top"
              size="medium"
              appearance="neutral"
            />
          </PositionAlignButton>
          <PositionAlignButton
            appearance="neutral"
            mode="tinted"
            size="small"
            selected={selectedPreset === 'center'}
            onClick={() => updateNormalizedVerticalPosition(POSITION_CENTER)}
          >
            <Icon
              name="StateBar-Position-Center"
              size="medium"
              appearance="neutral"
            />
          </PositionAlignButton>
          <PositionAlignButton
            appearance="neutral"
            mode="tinted"
            size="small"
            selected={selectedPreset === 'bottom'}
            onClick={() => updateNormalizedVerticalPosition(POSITION_BOTTOM)}
          >
            <Icon
              name="StateBar-Position-Bottom"
              size="medium"
              appearance="neutral"
            />
          </PositionAlignButton>
        </PositionButtonGroup>
      </Stack>
    </Box>
  );
}

function FineTuneSection({
  updateNormalizedVerticalPosition,
  editedStyle,
}: Pick<
  PositionSectionProps,
  'updateNormalizedVerticalPosition' | 'editedStyle'
>) {
  const normalizedValue =
    editedStyle.captionsStyle.normalizedVerticalPosition ??
    DEFAULT_NORMALIZED_VERTICAL_POSITION;
  const percentValue = Math.round(normalizedValue * 100);

  return (
    <Box sx={{marginTop: '24px'}}>
      <Stack direction="row" justifyContent="space-between" mb="8px">
        <SectionLabel size="md" sx={{mb: 0}}>
          {translate('Position vertical')}
        </SectionLabel>
        <SectionLabel size="md" sx={{mb: 0}}>
          {percentValue}%
        </SectionLabel>
      </Stack>
      <StyledSlider
        value={percentValue}
        min={0}
        max={100}
        onChange={(_, value) => {
          updateNormalizedVerticalPosition((value as number) / 100);
        }}
      />
    </Box>
  );
}

function PositionSection({
  updateNormalizedVerticalPosition,
  editedStyle,
}: PositionSectionProps) {
  return (
    <Box>
      <Stack gap="16px">
        <PositionPresetsSection
          updateNormalizedVerticalPosition={updateNormalizedVerticalPosition}
          editedStyle={editedStyle}
        />
        <FineTuneSection
          updateNormalizedVerticalPosition={updateNormalizedVerticalPosition}
          editedStyle={editedStyle}
        />
      </Stack>
    </Box>
  );
}

export default PositionSection;
